import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { INCOMMODITY_SATISFACTION_MANAGER } from "../../../../consts/incommodity";

import {
  formatDiffSecondToString,
  getDateRangeString,
  remainTime,
} from "../../../../libs/dateFormat";

import {
  RENT_REQUEST_TIMEOUT,
  RentStatus,
  StepRentStatus,
} from "../../../../types/rentTypes";

import { useUserContext } from "../../../../contexts/userContext";

import {
  ManagerGoodsRentalItem,
  completeManagerGoodsRental,
  getManagerGoodsRentalDetail,
  lostDamagedManagerGoodsRental,
  pushManagerGoodsRental,
  rentManagerGoodsRental,
  restoreManagerGoodsRental,
} from "../../../../services/managerGoodsRentalServices";
import { RentalStockGoodsTypeStrings } from "../../../../services/rentalStockServices";
import { SatisfactionRequest } from "../../../../services/userReportServices";

import useSimplePopup from "../../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";
import useVirtualNumber from "../../../../hooks/useVirtualNumber";

import StickyHeader from "../../../../components/Layout/StickyHeader";
import ProgressBar from "../../../../components/ProgressBar";
import HorizontalRule from "../../../../components/Rule/Horizontal";
import CommonButton from "../../../../components/Button";
import CommonPopup from "../../../../components/Popup";
import BottomGroup from "../../../../components/Button/Group";
import HistorySatisfaction, {
  SatisfactionReplyItem,
} from "../../../../components/History/Satisfaction";

import { ManagerReportDetailCommentIcon } from "../../Report/Detail";

type Props = {};

export default function ManagerRentalManagerDetail(props: Props) {
  const navigate = useNavigate();
  const params = useParams();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [detail, setDetail] = React.useState<ManagerGoodsRentalItem>();
  const [simplePopupComponent, { open }] = useSimplePopup({});
  const [popup, setPopup] = React.useState<
    | "checkManualRental"
    | "checkLostDamaged"
    | "checkManualReturn"
    | "checkRestore"
    | "checkPush"
    | ""
  >("");
  const [user] = useUserContext();
  const [virtualNumberHandler, virtualNumberComponent] = useVirtualNumber({});

  const [pushMessage, setPushMessage] = useState<string>("");
  const getPushMessage = (item: ManagerGoodsRentalItem) => {
    return `대여하신 ${item.goodsName}이 ${formatDiffSecondToString(
      Math.abs(remainTime(item.returnDueAt ?? "", 0))
    )} 연체 되었어요.\n빌려 가신 ${item.goodsName.trim()}은 다른 분들도 함께 쓰는 소중한 물건이랍니다. :)`;
  };

  const loadRentalDetail = async () => {
    setIsLoading(true);
    if (!params.id) return;
    try {
      const result = await getManagerGoodsRentalDetail(params.id);
      if (result) {
        setDetail(result.data);
      } else {
        open("대여 상세 조회에 실패했습니다.");
      }
    } catch (e: any) {
      console.log(e);
      if (e?.response?.data?.error) {
        open("ManagerRentalDetail " + e.response.data.error);
      } else {
        open("대여 상세 조회에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getRemainTime = (item?: ManagerGoodsRentalItem) => {
    if (!item) return "";

    switch (item.rentalServiceStatus) {
      case "REQUESTED":
        return `(신청 예약 ${formatDiffSecondToString(
          remainTime(item.createdAt, RENT_REQUEST_TIMEOUT)
        )} 남음)`;
      case "RENTED":
        return `${getDateRangeString(
          item.rentalAt ?? "",
          item.returnDueAt ?? ""
        )} (${formatDiffSecondToString(
          remainTime(item.returnDueAt ?? "", 0)
        )} 남음)`;
      case "DONE":
      case "COMPLETED":
        return `${getDateRangeString(
          item.rentalAt ?? "",
          item.actualReturnAt ?? ""
        )}`;
      case "LOST_DAMAGED":
        return `${getDateRangeString(
          item.rentalAt ?? "",
          item.lostDamagedAt ?? ""
        )}`;
      case "OVERDUE":
        return (
          <>
            {item.returnDueAt}
            <span
              className="text-brand-primary-red"
              data-testid="span-timeout"
            >{`(${formatDiffSecondToString(
              Math.abs(remainTime(item.returnDueAt ?? "", 0))
            )} 초과)`}</span>
          </>
        );
      default:
        return "";
    }
  };

  const handleManualRental = async () => {
    setIsLoading(true);
    try {
      const result = await rentManagerGoodsRental(detail?.goodsServiceId ?? "");
      if (result.status === HttpStatusCode.Ok) {
        loadRentalDetail();
      } else {
        open("수동 대여에 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error);
      } else {
        open("수동 대여에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualReturn = async () => {
    setIsLoading(true);
    try {
      const result = await completeManagerGoodsRental(
        detail?.goodsServiceId ?? ""
      );
      if (result.status === HttpStatusCode.Ok) {
        loadRentalDetail();
      } else {
        open("수동 반납에 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error);
      } else {
        open("수동 반납에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLostDamaged = async () => {
    setIsLoading(true);
    try {
      const result = await lostDamagedManagerGoodsRental(
        detail?.goodsServiceId ?? ""
      );
      if (result.status === HttpStatusCode.Ok) {
        loadRentalDetail();
      } else {
        open("분실/손상 처리에 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error);
      } else {
        open("분실/손상 처리에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestore = async () => {
    setIsLoading(true);
    try {
      const result = await restoreManagerGoodsRental(
        detail?.goodsServiceId ?? ""
      );
      if (result.status === HttpStatusCode.Ok) {
        loadRentalDetail();
      } else {
        open("정상 반납 처리에 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error);
      } else {
        open("정상 반납 처리에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePush = async () => {
    setIsLoading(true);
    try {
      const result = await pushManagerGoodsRental(
        detail?.goodsServiceId ?? "",
        pushMessage
      );
      if (result.status === HttpStatusCode.Ok) {
        loadRentalDetail();
      } else {
        open("반납 알림 발송에 실패했습니다.");
      }
    } catch (e: any) {
      if (e?.response?.data?.error) {
        open(e.response.data.error);
      } else {
        open("반납 알림 발송에 실패했습니다.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getActionButtons = (item?: ManagerGoodsRentalItem) => {
    if (!item) return "";

    switch (item.rentalServiceStatus) {
      case "REQUESTED":
        return (
          <CommonButton
            variant="primary"
            className="flex-1 h-bottom-button rounded-none"
            onClick={async () => {
              setPopup("checkManualRental");
            }}
          >
            수동 대여
          </CommonButton>
        );
      case "RENTED":
      case "OVERDUE":
        return (
          <>
            <CommonButton
              variant="secondary"
              className="flex-1 h-bottom-button rounded-none"
              onClick={async () => {
                setPopup("checkLostDamaged");
              }}
            >
              분실/손상 처리
            </CommonButton>
            <CommonButton
              variant="primary"
              className="flex-1 h-bottom-button rounded-none"
              onClick={async () => {
                setPopup("checkManualReturn");
              }}
            >
              수동 반납
            </CommonButton>
          </>
        );
      case "LOST_DAMAGED":
        return (
          <CommonButton
            variant="primary"
            className="flex-1 h-bottom-button rounded-none"
            onClick={async () => {
              setPopup("checkRestore");
            }}
          >
            정상 반납 처리
          </CommonButton>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (user.isLoading) return;
    loadRentalDetail();
  }, [user.isLoading]);

  return (
    <div>
      {loadingComponent}
      <StickyHeader
        title="대여 상세"
        className="px-5"
        onBackClick={() => navigate("/manager/rental-manager")}
      />

      <div className="flex flex-col gap-2.5 p-5">
        <div className="text-3xl">{detail?.goodsName}</div>
        <div className="text-base font-normal text-brand-neutral-40">
          {detail?.rentalBuildingName} {detail?.rentalBuildingFloorName}{" "}
          {detail?.rentalStoreName}
        </div>
      </div>

      <div className="flex flex-col gap-2.5 pt-0 px-5 pb-[30px]">
        <div
          className="p-5 flex flex-col gap-2.5 rounded-[10px]"
          style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.10)" }}
        >
          <div className=" bg-brand-floating-primary-button bg-clip-text text-base text-transparent font-semibold">
            {
              RentStatus[
                (detail?.rentalServiceStatus ??
                  "CANCELED") as keyof typeof RentStatus
              ]
            }
          </div>
          <ProgressBar
            currentStep={
              StepRentStatus[
                (detail?.rentalServiceStatus ??
                  "REQUESTED") as keyof typeof StepRentStatus
              ]
            }
            wrapperClassName="!p-0"
            className="h-2.5 !bg-brand-floating-primary-button"
            rounded
            nonSticky
            totalStep={3}
          />

          {["RENTED", "COMPLETED"].includes(
            detail?.rentalServiceStatus ?? ""
          ) && (
            <>
              <HorizontalRule className="!mt-[5px] !mb-[5px]" />
              {detail?.satisfactionInfo ? (
                <HistorySatisfaction
                  satisfaction={{
                    id: detail.satisfactionInfo.etcSatisfactionId,
                    score: detail.satisfactionInfo.satisfaction,
                    comment: detail.satisfactionInfo.comment,
                    createdAt: detail.satisfactionInfo.createdAt,
                    createdBy: detail.satisfactionInfo.createdBy,
                    createdByName: detail.satisfactionInfo.createdByName,
                    createdByThumbnail:
                      detail.satisfactionInfo.memberProfileImg?.items.find(
                        (item) => item.scale === "medium"
                      )?.url ?? null,
                    reply:
                      detail.satisfactionInfo.reply?.etcSatisfactionId &&
                      detail.satisfactionInfo.reply?.etcSatisfactionId > 0
                        ? ({
                            id: detail.satisfactionInfo.reply
                              ?.etcSatisfactionId,
                            comment: detail.satisfactionInfo.reply?.comment,
                            createdAt: detail.satisfactionInfo.reply?.createdAt,
                            createdBy: detail.satisfactionInfo.reply?.createdBy,
                            createdByName:
                              INCOMMODITY_SATISFACTION_MANAGER.NAME,
                            createdByThumbnail:
                              detail.satisfactionInfo.reply?.memberProfileImg?.items.find(
                                (item) => item.scale === "medium"
                              )?.url,
                          } as SatisfactionReplyItem)
                        : undefined,
                  }}
                  type="rental"
                  serviceId={detail.goodsServiceId}
                  isManager
                  onLoad={loadRentalDetail}
                  postSatisfaction={async (
                    satisfactionId: number,
                    satisfaction: SatisfactionRequest
                  ) => {
                    return;
                  }}
                />
              ) : (
                <div className="flex flex-col gap-2.5 items-center justify-center">
                  <ManagerReportDetailCommentIcon />
                  <div className=" text-brand-neutral-60 text-sm font-bold">
                    등록된 평가가 없어요
                  </div>
                </div>
              )}
            </>
          )}

          <div>
            <HorizontalRule className="!mt-[5px] !mb-[15px]" />
            <div className="flex items-center gap-2.5 justify-between">
              <div className="flex flex-col gap-[5px] w-full">
                <div className="text-sm font-semibold leading-[19px]">
                  {detail?.createdByName}
                </div>
                <div className="text-xs text-brand-neutral-30 leading-4">
                  {detail?.createdByCompanyName}
                </div>
              </div>
              <div className="flex gap-1.5 items-center design:flex-wrap">
                {detail?.rentalServiceStatus === "OVERDUE" && (
                  <CommonButton
                    variant="tertiary"
                    className="h-[34px] px-4 py-[11px] flex items-center justify-center text-sm font-medium leading-[17px] text-brand-neutral-60 whitespace-nowrap design_min:w-[120px]"
                    onClick={() => {
                      setPushMessage(getPushMessage(detail));
                      setPopup("checkPush");
                    }}
                  >
                    반납 요청 발송
                  </CommonButton>
                )}
                <CommonButton
                  variant="tertiary"
                  className="h-[34px] px-4 py-[11px] flex items-center justify-center text-sm font-medium leading-[17px] text-brand-neutral-60 whitespace-nowrap design_min:w-[120px]"
                  onClick={() => {
                    if (detail?.createdByMobileNumber) {
                      window.location.href = `tel:${detail.createdByMobileNumber}`;
                    } else {
                      open("대여자의 전화번호 정보가 없습니다.");
                    }
                  }}
                >
                  대여자 연결
                </CommonButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-5 flex flex-col gap-[30px]">
        <div className="flex flex-col border-t border-black">
          <div className="pt-[30px]">
            <div className="flex flex-col gap-5 text-lg font-medium leading-[21px] pb-5">
              대여 정보
            </div>

            <div className="flex py-1.5 gap-2.5">
              <div className="w-[90px] py-3 flex flex-col gap-2.5 text-brand-neutral-60 text-sm leading-[18px]">
                분류
              </div>
              <div className="flex flex-col gap-[5px] flex-[1_0_0] p-2.5">
                <div className="text-base font-bold leading-[26px]">
                  {
                    RentalStockGoodsTypeStrings[
                      detail?.goodsTypeId ?? "UMBRELLA"
                    ]
                  }
                </div>
                <div className="text-base leading-[26px]">
                  {detail?.goodsName}
                </div>
              </div>
            </div>

            <div className="flex py-1.5 gap-2.5">
              <div className="w-[90px] py-3 flex flex-col gap-2.5 text-brand-neutral-60 text-sm leading-[18px]">
                대여/반납 장소
              </div>
              <div className="flex flex-col gap-[5px] flex-[1_0_0] p-2.5">
                <div className="text-base leading-[26px] whitespace-pre-line">
                  {`${detail?.rentalBuildingName ?? ""}\n${
                    detail?.rentalBuildingFloorName ?? ""
                  } ${detail?.rentalStoreName ?? ""}`}
                </div>
              </div>
            </div>

            <div className="flex py-1.5 gap-2.5">
              <div className="w-[90px] py-3 flex flex-col gap-2.5 text-brand-neutral-60 text-sm leading-[18px]">
                대여자 성명
              </div>
              <div className="flex flex-col gap-[5px] flex-[1_0_0] p-2.5">
                <div className="text-base leading-[26px] whitespace-pre-line">
                  {detail?.createdByName ?? ""}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col border-t border-black">
          <div className="pt-[30px]">
            <div className="flex flex-col gap-5 text-lg font-medium leading-[21px] pb-5">
              신청 내역
            </div>

            <div className="flex py-1.5 gap-2.5">
              <div className="w-[90px] py-3 flex flex-col gap-2.5 text-brand-neutral-60 text-sm leading-[18px]">
                신청 시간
              </div>
              <div className="flex flex-col gap-[5px] flex-[1_0_0] p-2.5">
                <div className="text-base leading-[26px] whitespace-pre-line">
                  {`${detail?.createdAt ?? ""}${
                    detail?.rentalServiceStatus === "REQUESTED"
                      ? `\n${getRemainTime(detail)}`
                      : ""
                  }`}
                </div>
              </div>
            </div>

            {StepRentStatus[
              (detail?.rentalServiceStatus ??
                "REQUESTED") as keyof typeof StepRentStatus
            ] >= 2 &&
              detail?.rentalAt && (
                <div className="flex py-1.5 gap-2.5">
                  <div className="w-[90px] py-3 flex flex-col gap-2.5 text-brand-neutral-60 text-sm leading-[18px]">
                    대여 시간
                  </div>
                  <div className="flex flex-col gap-[5px] flex-[1_0_0] p-2.5">
                    <div className="text-base leading-[26px] whitespace-pre-line">
                      {detail?.rentalAt}
                    </div>
                  </div>
                </div>
              )}

            {StepRentStatus[
              (detail?.rentalServiceStatus ??
                "REQUESTED") as keyof typeof StepRentStatus
            ] >= 3 &&
              (detail?.returnDueAt || detail?.actualReturnAt) && (
                <div className="flex py-1.5 gap-2.5">
                  <div className="w-[90px] py-3 flex flex-col gap-2.5 text-brand-neutral-60 text-sm leading-[18px]">
                    반납 시간
                  </div>
                  <div className="flex flex-col gap-[5px] flex-[1_0_0] p-2.5">
                    <div className="text-base leading-[26px] whitespace-pre-line">
                      {`${detail?.actualReturnAt} ${
                        ["OVERDUE"].includes(detail.rentalServiceStatus)
                          ? getRemainTime(detail)
                          : ""
                      }`}
                    </div>
                  </div>
                </div>
              )}

            {detail?.rentalServiceStatus === "CANCELED" && (
              <div className="flex py-1.5 gap-2.5">
                <div className="w-[90px] py-3 flex flex-col gap-2.5 text-brand-neutral-60 text-sm leading-[18px]">
                  취소 시간
                </div>
                <div className="flex flex-col gap-[5px] flex-[1_0_0] p-2.5">
                  <div className="text-base leading-[26px] whitespace-pre-line">
                    {detail?.canceledAt}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {!["DONE", "COMPLETED", "CANCELED"].includes(
        detail?.rentalServiceStatus ?? ""
      ) && (
        <div
          className="flex items-center fixed bottom-0 left-0 right-0"
          data-testid="action-button-group"
        >
          {getActionButtons(detail)}
        </div>
      )}

      <CommonPopup isOpen={popup === "checkManualRental"} unUseHorizonRule>
        <div className="pt-5 whitespace-pre-line text-center">{`${detail?.goodsName} (${detail?.createdByName})\n수동 대여 처리 하시겠어요?`}</div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-[100px] rounded-none"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              disabled={isLoading}
              className="w-full rounded-none"
              variant="primary"
              onClick={() => {
                handleManualRental();
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>

      <CommonPopup isOpen={popup === "checkLostDamaged"} unUseHorizonRule>
        <div className="pt-5 whitespace-pre-line text-center">{`${detail?.goodsName} (${detail?.createdByName})\n분실/손상 처리 하시겠어요?`}</div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-[100px] rounded-none"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              disabled={isLoading}
              className="w-full rounded-none"
              variant="primary"
              onClick={() => {
                handleLostDamaged();
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>

      <CommonPopup isOpen={popup === "checkManualReturn"} unUseHorizonRule>
        <div className="pt-5 whitespace-pre-line text-center">{`${detail?.goodsName} (${detail?.createdByName})\n수동 반납 처리 하시겠어요?`}</div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-[100px] rounded-none"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              disabled={isLoading}
              className="w-full rounded-none"
              variant="primary"
              onClick={() => {
                handleManualReturn();
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>

      <CommonPopup isOpen={popup === "checkRestore"} unUseHorizonRule>
        <div className="pt-5 whitespace-pre-line text-center">{`${detail?.goodsName} (${detail?.createdByName})\n정상 반납 처리 하시겠어요?`}</div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-[100px] rounded-none"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              disabled={isLoading}
              className="w-full rounded-none"
              variant="primary"
              onClick={() => {
                handleRestore();
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>

      <CommonPopup
        isOpen={popup === "checkPush"}
        unUseHorizonRule
        title="반납 알림 발송"
      >
        <div className="pb-5 whitespace-pre-line text-center text-xl leading-[24px] font-medium">{`${detail?.goodsName} (${detail?.createdByName})\n반납 알림을 발송하시겠어요?`}</div>
        <div className="flex flex-col gap-2.5">
          <div className="text-sm font-medium">발송 안내 내용</div>
          <div className=" bg-brand-main-page-background p-[15px] rounded-[5px] whitespace-pre-line text-sm leading-[24px] text-brand-neutral-70">
            {pushMessage}
          </div>
        </div>
        <div className="pb-[50px]"></div>
        <BottomGroup zIndex="high">
          <div className="flex flex-row h-bottom-button">
            <CommonButton
              variant="grey"
              className="w-[100px] rounded-none"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              disabled={isLoading}
              className="w-full rounded-none"
              variant="primary"
              onClick={() => {
                handlePush();
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </div>
        </BottomGroup>
      </CommonPopup>
      {simplePopupComponent}
      {virtualNumberComponent}
    </div>
  );
}
